import PropTypes from "prop-types";
import React, { useCallback, useEffect, useState } from "react";

import styled from "styled-components/macro";

import { useLoginLevel } from "Flocknote/stateHooks";

import API from "Signups/API.js";
import { getSignupErrorMessage } from "Signups/SharedComponents/getSignupErrorMessage";
import { useIsSignupClosed } from "Signups/SharedComponents/useIsSignupClosed";
import { useSignups } from "Signups/state";
import { AnimatePresence, motion } from "framer-motion";

import FnrButton from "UI/FnrButton3";
import { colors } from "UI/colors";

import CancelMemberSignup from "./CancelMemberSignup";
import CostInfoBadge from "./CostInfoBadge";
import CurrentlySignedUp from "./CurrentlySignedUp";
import Error from "./Error";
import NewUserForm from "./NewUserForm";
import ProgressBarArea from "./ProgressBarArea";
import RespondentsList from "./RespondentsList";
import {
  SignupItemWrapper,
  Title,
  TitleBar,
  TitleButtonWrapper,
} from "./Styles";
import { useCancelSignupItem } from "./apiHooks";
import { useShowNewUserForm } from "./stateHooks";
import checkIfFullyPaid from "./utils/checkIfFullyPaid";
import { composeHouseholdSubmitData } from "./utils/composeHouseholdSubmitData";
import { composeInitialRespondents } from "./utils/composeInitialRespondents";
import { composeModifyHouseholdSubmitData } from "./utils/composeModifyHouseholdSubmitData";
import { composeNonHouseholdSubmitData } from "./utils/composeNonHouseholdSubmitData";
import {
  getExtendedFieldResponsesValidationErrors,
  getSharedExtendedFieldResponsesValidationErrors,
} from "./utils/fieldValidationHelpers";
import { useIsSignedUpForItem } from "./utils/useIsSignedUpForItem";

SignupItemMembersType.propTypes = {
  signupItem: PropTypes.object,
};

export default function SignupItemMembersType({ signupItem }) {
  const [state, dispatch] = useSignups();
  const {
    expandedItemID,
    extendedFieldInputResponses,
    sharedExtendedFieldInputResponses,
  } = state;
  const { ID, title, totalSlots, slotsTaken, extendedFields } = signupItem;
  const setExpandedItemID = useCallback(
    (ID) => {
      dispatch({
        type: "expandedItemID",
        ID,
      });
    },
    [dispatch]
  );

  const handleCloseItem = useCallback(
    () => setExpandedItemID(null),
    [setExpandedItemID]
  );
  const [cancelIsLoading, cancelSignup] = useCancelSignupItem({
    signupItemID: ID,
    closeItem: handleCloseItem,
  });

  const isExpanded = expandedItemID === ID;
  const [status, setStatus] = useState(null);

  // syncs the cancel status with the overall status
  useEffect(() => {
    if (status) return; // do not override another status
    if (cancelIsLoading) setStatus(cancelIsLoading ? "loading" : null);
  }, [cancelIsLoading, status]);

  const [errorMessage, setErrorMessage] = useState(null);
  const [signupErrorMessage, setSignupErrorMessage] = useState(null);
  // This initial respondents is not really the actual respondents, but...
  // ...all potential respondents: a mash up of the current user, household members, and additional names
  // After mashing them all up, this compose function then runs through and disables/checks as necessary
  const isCurrentlySignedUp = useIsSignedUpForItem(ID);
  const [respondents, setRespondents] = useState(() =>
    composeInitialRespondents(state, signupItem, isCurrentlySignedUp)
  );
  const { isClosed } = useIsSignupClosed();
  const isFull = totalSlots - slotsTaken < 1;

  useEffect(() => {
    setStatus(null);
  }, [isExpanded]);

  const showNewUserForm = useShowNewUserForm(extendedFields?.length > 0);

  const resetRespondents = useCallback(
    () =>
      setRespondents(
        composeInitialRespondents(state, signupItem, isCurrentlySignedUp)
      ),
    [signupItem, state, isCurrentlySignedUp]
  );

  useEffect(() => {
    if (state.triggerRespondentRefresh) {
      resetRespondents();
      dispatch({ type: "updateTriggerRespondentRefresh", payload: false });
    }
  }, [
    state.user,
    state.triggerRespondentRefresh,
    state,
    signupItem,
    dispatch,
    resetRespondents,
  ]);

  const setRespondentsWithDispatch = (resp) => {
    dispatch({ type: "setMemberRespondents", payload: resp });
    setRespondents(resp);
  };

  useEffect(() => {
    // auto-clears the error message after a few seconds
    if (errorMessage) {
      setTimeout(() => {
        setErrorMessage(null);
      }, 3000);
    }
  }, [errorMessage, setErrorMessage]);

  const setFieldValidationError = (errors) => {
    dispatch({ type: "setFieldValidationErrors", payload: errors });
  };

  const setSharedFieldValidationError = (errors) => {
    dispatch({ type: "setSharedFieldValidationErrors", payload: errors });
  };

  const getSelectedInfo = () => {
    const num = respondents.filter((r) => r.isChecked).length;

    if (num === 1) return "1 person selected";
    else return `${num} people selected`;
  };

  const handleSubmitClick = async () => {
    setFieldValidationError({});
    setSharedFieldValidationError([]);
    const needsName = respondents.filter(
      (r) => r.isAdditionalName && r.name.trim() === ""
    ).length;
    if (needsName)
      return setErrorMessage("Please enter a name for everyone signing up.");

    // starting load state now in case files start being uploaded.
    setStatus("loading");

    const fieldValidationErrors =
      await getExtendedFieldResponsesValidationErrors(
        respondents,
        extendedFields,
        extendedFieldInputResponses
      );
    const sharedFieldValidationErrors =
      await getSharedExtendedFieldResponsesValidationErrors(
        extendedFields,
        sharedExtendedFieldInputResponses
      );
    if (fieldValidationErrors || sharedFieldValidationErrors) {
      setFieldValidationError(fieldValidationErrors || {});
      setSharedFieldValidationError(sharedFieldValidationErrors || []);
      setStatus(null);
      return;
    }

    const numOfPrimaryOrHouseholdUsers = respondents.filter(
      (r) => r.isChecked && !r.isAdditionalName
    ).length;
    const householdUsersExist =
      respondents.filter((r) => !r.isAdditionalName).length > 1;

    // confirm someone else with a userID will still be checked
    if (numOfPrimaryOrHouseholdUsers < 1) {
      if (isCurrentlySignedUp) {
        cancelSignup();
        return;
      }
      setStatus(null);
      return setErrorMessage(
        householdUsersExist
          ? `Someone from your household must be selected.`
          : `You must be selected.`
      );
    }

    const isHouseholdSignup = state.user.canSignupHouseholdMembers;

    if (isHouseholdSignup && isCurrentlySignedUp) {
      submitModifyHouseholdSignup();
    } else if (isHouseholdSignup) {
      submitHouseholdSignup();
    } else {
      // note: for non-household signups, this can handle both new signups and modifying existing signups.
      submitNonHouseholdSignup();
    }
  };

  const submitHouseholdSignup = async () => {
    const submitData = await composeHouseholdSubmitData({
      respondents,
      signupItemID: ID,
      householdID: state.user.householdID,
      extendedFieldInputResponses,
      sharedExtendedFieldInputResponses,
      extendedFields,
    });
    API.signupHouseholdUsersForItem(submitData)
      .post()
      .then((res) => {
        handleSuccessfulResponse(res);
      })
      .catch((err) => {
        handleErrorResponse(err);
      });
  };

  const submitModifyHouseholdSignup = async () => {
    const submitData = await composeModifyHouseholdSubmitData({
      state,
      respondents,
      signupItemID: ID,
      householdID: state.user.householdID,
      extendedFieldInputResponses,
      sharedExtendedFieldInputResponses,
      extendedFields,
    });
    API.modifyHouseholdUserSignups(submitData)
      .post()
      .then((res) => {
        handleSuccessfulResponse(res);
      })
      .catch((err) => {
        handleErrorResponse(err);
      });
  };

  const submitNonHouseholdSignup = async () => {
    const submitData = await composeNonHouseholdSubmitData({
      respondents,
      signupItemID: ID,
      extendedFieldInputResponses,
      sharedExtendedFieldInputResponses,
      extendedFields,
    });
    API.signupForItem2(submitData)
      .post()
      .then((res) => {
        handleSuccessfulResponse(res);
      })
      .catch((err) => {
        handleErrorResponse(err);
      });
  };

  const handleSuccessfulResponse = (res) => {
    dispatch({
      type: "Item Signup Updated",
      userSignupItems: res.userSignupItems,
      newCounts: res.itemSlotsTaken,
      cancelled: false,
      paymentAmountNeeded: 0, // hides Pay Now while price recalculates
    });

    // clear flags used for autoselecting just-added HH members
    dispatch({ type: "clearWasJustAddedToHHFlags" });

    // Don't show confetti if this requires a payment and the user hasn't paid for everyone they've signed up
    const isFullyPaid = checkIfFullyPaid(res.userSignupItems);

    const hasSignupCost =
      signupItem?.hasSignupCost ||
      state?.currentSignupList?.fundIt?.hasSignupCost;

    if (hasSignupCost && !isFullyPaid) {
      dispatch({
        type: "showConfetti",
        payload: false,
      });
      setStatus(null);
      setExpandedItemID(null);
      return;
    }

    // Otherwise, show confetti
    dispatch({
      type: "showConfetti",
      payload: true,
    });

    dispatch({ type: "updateTriggerRespondentRefresh", payload: true });

    setStatus(null);
    setExpandedItemID(null);
  };

  const handleErrorResponse = (err) => {
    if (err.error === "login_required") {
      // The main use case for "login_required" is if the user is not currently logged in, but have already signed up for this Signup list.
      // forcing them to log in allows them to accurately modify their existing signup.
      window.location = `/login?redirect=signup/${state.currentSignupList.ID}`;
      return;
    }

    setStatus("error");

    const errorCode = err.error;
    setSignupErrorMessage(getSignupErrorMessage(errorCode));
    if (errorCode === "no_user") {
      // this error code is often caused by an invalid email/phone, so we clear the user.
      dispatch({
        type: "user",
        payload: null,
      });
    }
  };

  const closeSignupItem = () => {
    setExpandedItemID(null);
    setErrorMessage(null);
    setSignupErrorMessage(null);
  };

  const SignupButton = () => {
    if (isCurrentlySignedUp || isClosed || isFull) return null;

    const buttonText =
      state.currentSignupList?.signupListVerbs?.actionName || "Sign Up";

    return (
      <FnrButton
        color="blue"
        data-cy="public-signup-button"
        onClick={() => setExpandedItemID(ID)}
      >
        {buttonText}
      </FnrButton>
    );
  };

  const SubmitButton = () => (
    <FnrButton
      color="green"
      data-cy="public-signup-submit-button"
      onClick={handleSubmitClick}
      loading={status === "loading"}
    >
      {getSubmitButtonText()}
    </FnrButton>
  );

  const getSubmitButtonText = () => {
    if (isCurrentlySignedUp) return "Update Signup";

    // check to see if its an anonymous user, or if the currently logged in user is selected
    const userIsSigningUp =
      state.user?.emailOrPhone ||
      respondents
        .filter((r) => r.isChecked)
        .find((r) => r.userID === state.user?.ID);

    const multipleSignups = respondents.filter((r) => r.isChecked).length > 1;
    const noOneSignedUp = respondents.filter((r) => r.isChecked).length < 1;

    if (noOneSignedUp) return "Sign Up!";
    else if (!userIsSigningUp && !multipleSignups) return "Sign Them Up!";
    else if (userIsSigningUp && !multipleSignups) return "Sign Me Up!";
    else if (userIsSigningUp && multipleSignups) return "Sign Us Up!";
    else return "Sign Up!";
  };

  const renderExpandedContent = () => {
    if (status === "error") {
      return (
        <Error
          message={signupErrorMessage}
          handleCloseClick={closeSignupItem}
        />
      );
    } else if (showNewUserForm) {
      return <NewUserForm handleCloseClick={closeSignupItem} />;
    }

    return (
      <ExpandedWrapper>
        <AnimatePresence>
          {errorMessage ? (
            <Alert
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {errorMessage}
            </Alert>
          ) : null}
        </AnimatePresence>

        <RespondentsList
          signupItem={signupItem}
          respondents={respondents}
          setRespondents={setRespondentsWithDispatch}
          resetRespondents={resetRespondents}
          setErrorMessage={setErrorMessage}
          handleCloseClick={closeSignupItem}
          SubmitButton={SubmitButton}
        />
        <SubmitButtonWrapper>
          <CurrentlySelected>
            {getSelectedInfo()}
            <CancelMemberSignup signupItemID={ID} closeItem={handleCloseItem} />
          </CurrentlySelected>
          {/* <SubmitButton /> */}
        </SubmitButtonWrapper>
      </ExpandedWrapper>
    );
  };

  return (
    <SignupItemWrapper>
      <TitleBar>
        <Title>{title}</Title>
        <TitleButtonWrapper>
          {isExpanded ? <div /> : SignupButton()}
        </TitleButtonWrapper>
      </TitleBar>

      <CostInfoBadge
        signupItem={signupItem}
        isCurrentlySignedUp={isCurrentlySignedUp}
      />

      {isExpanded ? (
        <motion.div
          initial={{ y: -30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          {renderExpandedContent()}
        </motion.div>
      ) : (
        <>
          {isCurrentlySignedUp ? (
            <CurrentlySignedUp signupItem={signupItem} signupType="member" />
          ) : null}
          <ProgressBarArea signupItem={signupItem} />
        </>
      )}
    </SignupItemWrapper>
  );
}

const ExpandedWrapper = styled.div`
  position: relative;
`;

const Alert = styled(motion.div)`
  z-index: 100;
  position: absolute;
  right: 10px;
  bottom: 66px;
  background-color: ${colors.fnPurple};
  color: white;
  font-weight: 400;
  font-size: 14px;
  padding: 10px;
  width: 140px;
  border-radius: 8px;
`;

const SubmitButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
  }
`;

const CurrentlySelected = styled.div`
  font-size: 15px;
  color: ${colors.fnDkBlue};

  @media (max-width: 480px) {
    margin-bottom: 10px;
    align-self: flex-start;
  }
`;
